// ============================================================
// Tome System Constants — Rogue-like drop & pick system
// ============================================================
export const TOME_DEFINITIONS = {
    xp: {
        id: 'xp',
        name: 'Book of Experience',
        description: '+5% XP from all sources',
        icon: '⚜',
        color: '#22c55e',
        bonusPerLevel: 0.05,
        bonusUnit: '% XP',
        maxLevel: 99,
    },
    luck: {
        id: 'luck',
        name: 'Book of Fortune',
        description: '+5% loot, gold & material drops',
        icon: '❂',
        color: '#eab308',
        bonusPerLevel: 0.05,
        bonusUnit: '% Loot',
        maxLevel: 99,
    },
    cooldown: {
        id: 'cooldown',
        name: 'Book of Haste',
        description: '-2% ability cooldowns',
        icon: '⧖',
        color: '#3b82f6',
        bonusPerLevel: 0.02,
        bonusUnit: '% CDR',
        maxLevel: 99,
        requiresQuestId: 'haste_unlock',
    },
    cursed: {
        id: 'cursed',
        name: 'Cursed Book',
        description: 'Each rank thickens every wave. At rank 99, ten times the foes rise to meet you.',
        icon: '☠',
        color: '#8b2020',
        bonusPerLevel: 9 / 99, // rank 99 → ×9 extra = 10× total enemies
        bonusUnit: '% wave size',
        maxLevel: 99,
    },
    armor: {
        id: 'armor',
        name: 'Book of Fortitude',
        description: '+3 DEF for all heroes',
        icon: '⛨',
        color: '#3b6ea0',
        bonusPerLevel: 3,
        bonusUnit: ' DEF',
        maxLevel: 99,
    },
    damage: {
        id: 'damage',
        name: 'Book of Power',
        description: '+4 ATK for all heroes',
        icon: '⚔',
        color: '#ef4444',
        bonusPerLevel: 4,
        bonusUnit: ' ATK',
        maxLevel: 99,
    },
    speed: {
        id: 'speed',
        name: 'Book of Agility',
        description: '+5% hero attack speed',
        icon: '⇶',
        color: '#06b6d4',
        bonusPerLevel: 0.05,
        bonusUnit: '% ATK SPD',
        maxLevel: 99,
    },
    crit: {
        id: 'crit',
        name: 'Book of Precision',
        description: '+2% critical hit chance',
        icon: '✦',
        color: '#f43f5e',
        bonusPerLevel: 0.02,
        bonusUnit: '% Crit',
        maxLevel: 99,
    },
    health: {
        id: 'health',
        name: 'Book of Vitality',
        description: '+5% max HP for all heroes',
        icon: '♥',
        color: '#dc2626',
        bonusPerLevel: 0.05,
        bonusUnit: '% HP',
        maxLevel: 99,
    },
    regen: {
        id: 'regen',
        name: 'Book of Renewal',
        description: '+1.5 HP regen per second for all heroes',
        icon: '✚',
        color: '#16a34a',
        bonusPerLevel: 1.5,
        bonusUnit: ' HP/s',
        maxLevel: 99,
    },
    evasion: {
        id: 'evasion',
        name: 'Book of Evasion',
        description: '+0.5% dodge chance for all heroes (max 50%)',
        icon: '↻',
        color: '#2060a0',
        bonusPerLevel: 0.005,
        bonusUnit: '% Dodge',
        maxLevel: 99,
    },
    thorns: {
        id: 'thorns',
        name: 'Book of Thorns',
        description: 'Reflect 0.1% of damage taken per rank back to attacker',
        icon: '❀',
        color: '#be123c',
        bonusPerLevel: 0.001,
        bonusUnit: '% reflect',
        maxLevel: 99,
    },
    bloody: {
        id: 'bloody',
        name: 'Bloody Book',
        description: '+~0.34% lifesteal per rank (caps at 33.33%)',
        // Down-pointing triangle reads as a falling drop in gold tone — monochrome glyph that accepts the
        // unified tome colour (unlike emojis 💧/🩸 which always render in their own palette).
        icon: '▼',
        color: '#7f1d1d',
        // Math: 99 ranks × 0.003367 ≈ 0.3333 (33.33 %) — round4 in getTomeBonus keeps the
        // sum at exactly 0.3333 so the stat caps cleanly. Lets the player keep picking
        // Bloody across a long run without hitting "all chosen types maxed" too early.
        bonusPerLevel: 0.003367,
        bonusUnit: '% lifesteal',
        maxLevel: 99,
        requiresQuestId: 'vampirism_unlock',
    },
    gold: {
        id: 'gold',
        name: 'Book of Greed',
        description: '+8% gold from all sources',
        icon: '⦿',
        color: '#d97706',
        bonusPerLevel: 0.08,
        bonusUnit: '% Gold',
        maxLevel: 99,
    },
    spellpower: {
        id: 'spellpower',
        name: 'Book of Arcana',
        description: '+4 spell power for all heroes',
        icon: '✧',
        color: '#4080b0',
        bonusPerLevel: 4,
        bonusUnit: ' SP',
        maxLevel: 99,
    },
    chaos: {
        id: 'chaos',
        name: 'Book of Chaos',
        description: 'Random permanent stat boost on pick',
        icon: '✺',
        color: '#f472b6',
        bonusPerLevel: 1,
        bonusUnit: ' random',
        maxLevel: 99,
        requiresQuestId: 'chaos_unlock',
    },
    critDamage: {
        id: 'critDamage',
        name: 'Book of Devastation',
        description: '+4% crit damage multiplier per level',
        icon: '☄',
        color: '#fb923c',
        bonusPerLevel: 0.04,
        bonusUnit: '% Crit DMG',
        maxLevel: 99,
    },
    loot: {
        id: 'loot',
        name: 'Book of Plunder',
        description: '+5% chance for rare or better loot per level',
        icon: '◈',
        color: '#facc15',
        bonusPerLevel: 0.05,
        bonusUnit: '% rare loot',
        maxLevel: 99,
    },
    killHeal: {
        id: 'killHeal',
        name: 'Book of Bloodthirst',
        description: '+0.1 max HP per kill (permanent, stacks with book level)',
        icon: '⚕',
        color: '#dc2626',
        bonusPerLevel: 0.1,
        bonusUnit: ' maxHP/kill',
        maxLevel: 99,
        requiresQuestId: 'bloodthirst_unlock',
    },
    // Quest-gated tome — unlocked by completing "The Archive Beneath" in the Haunted Crypts.
    // Primary: +2 SP per rank. Secondary: resist school randomly rolled on first pick and
    // locked thereafter; +0.2 % resist per rank is credited to that one school (capped near
    // 20 % by the maxLevel of 99).
    book_archivist: {
        id: 'book_archivist',
        name: 'Book of the Archivist',
        description: '+2 Spell Power and +0.2% resist to a random school per pick',
        // Snowflake glyph reflects the Archivist lich's frost theme; gold tint keeps it consistent
        // with the rest of the tome icons.
        icon: '❄',
        color: '#c8973a',
        bonusPerLevel: 2,
        bonusUnit: ' SP',
        maxLevel: 99,
        requiresQuestId: 'archive_beneath',
    },
};
export const TOME_IDS = [
    'xp', 'luck', 'cooldown', 'cursed', 'armor', 'damage',
    'speed', 'crit', 'critDamage', 'health', 'regen', 'evasion',
    'thorns', 'bloody', 'gold', 'spellpower', 'chaos', 'loot', 'killHeal',
    'book_archivist',
];
/** Chance for a tome to drop from an elite kill (boss is always 100%) */
export const TOME_ELITE_DROP_CHANCE = 0.08;
//# sourceMappingURL=tomes.js.map